// ClipRadar website — shared primitives (logo, CTAs, section heads, demo chrome).
import { useState, type CSSProperties, type ReactNode } from 'react';
import { CRWIcon, type IconName } from './icons';
import { useTyping, useSettled } from './hooks';
import { useWaitlistSubmit, type WaitlistTier } from './use-waitlist-submit';
import logoMark from '../assets/logo-mark.svg';
import logoMarkMono from '../assets/logo-mark-mono.svg';

// The locked ClipRadar lockup — Clash Display 600, option-B gradient
// (#f4f5f7 25% → #4ae3ff 150%), +0.005em tracking. The gradient is brand law,
// independent of the site accent system — do not retheme it.
export function CRWLogo({ size = 26, text = true }: { size?: number; text?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <img src={logoMark} width={size} height={size} alt="" />
      {text ? (
        <b style={{
          fontFamily: "'Clash Display', var(--font-display)", fontSize: Math.round(size * 0.72), fontWeight: 600, letterSpacing: '0.005em',
          background: 'linear-gradient(90deg, #F4F5F7 25%, #4AE3FF 150%)',
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
        }}>
          ClipRadar
        </b>
      ) : null}
    </span>
  );
}

interface BtnProps {
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  style?: CSSProperties;
  disabled?: boolean;
  busy?: boolean;
}

// Premium white CTA (Linear/Raycast style).
export function GradBtn({ children, size = 'md', onClick, style, disabled, busy }: BtnProps) {
  const pad = size === 'lg' ? '14px 28px' : size === 'sm' ? '8px 16px' : '11px 22px';
  const fs = size === 'lg' ? 16 : size === 'sm' ? 13 : 14;
  const off = disabled || busy;
  return (
    <button className="crw-gradbtn" onClick={onClick} disabled={off} aria-busy={busy || undefined} style={{ ...(off ? { opacity: 0.55, cursor: 'not-allowed' } : null), padding: pad, fontSize: fs, ...style }}>
      {children}
    </button>
  );
}

// Metal morphism CTA — rotating chrome border, dark glass core. `neutral`
// renders the silver cut (for chrome placements like the nav, where a warm
// glow would break the one-warm-element law); content primaries keep the brass.
export function MetalBtn({ children, size = 'md', onClick, style, disabled, busy, neutral }: BtnProps & { neutral?: boolean }) {
  const pad = size === 'lg' ? '14px 30px' : size === 'sm' ? '8px 18px' : '12px 24px';
  const fs = size === 'lg' ? 15.5 : size === 'sm' ? 13 : 14;
  const off = disabled || busy;
  return (
    <button className={`crw-metal${neutral ? ' crw-metal-neutral' : ''}`} onClick={onClick} disabled={off} aria-busy={busy || undefined} style={{ ...(off ? { opacity: 0.55, cursor: 'not-allowed' } : null), ...style }}>
      <span style={{ padding: pad, fontSize: fs }}>{children}</span>
    </button>
  );
}

export function SectionHead({ label, title, sub, center, maxW = 620 }: { label?: string; title: ReactNode; sub?: ReactNode; center?: boolean; maxW?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: center ? 'center' : 'flex-start', textAlign: center ? 'center' : 'left', marginBottom: 44 }}>
      {label ? <span className="crw-eyebrow"><i></i>{label}</span> : null}
      <h2 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)', lineHeight: 1.08, textWrap: 'balance' }}>{title}</h2>
      {sub ? <p style={{ margin: 0, fontSize: 16.5, lineHeight: 1.65, color: 'var(--fg-2)', maxWidth: maxW, textWrap: 'pretty' }}>{sub}</p> : null}
    </div>
  );
}

// Fake search bar with typing + caret.
export function FakeSearch({ query, icon = 'search', chip, delay, height = 44 }: { query: string; icon?: IconName; chip?: string; delay?: number; height?: number }) {
  const { typed, done } = useTyping(query, { delay: delay || 500 });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height, padding: '0 14px', borderRadius: 8, background: 'var(--ink-900)', border: '1px solid var(--line-2)', boxShadow: done ? '0 0 0 1px rgba(var(--light-mid-rgb),0.3), 0 0 18px rgba(var(--light-rgb),0.12)' : 'none', transition: 'box-shadow 300ms var(--ease-out)' }}>
      <span style={{ color: 'var(--signal-400)', display: 'inline-flex', flex: 'none' }}><CRWIcon d={icon} size={15} /></span>
      {chip ? <span style={{ flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.05em', color: 'var(--signal-300)', background: 'var(--signal-dim)', padding: '3px 8px', borderRadius: 4 }}>{chip}</span> : null}
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--fg-1)', whiteSpace: 'nowrap', overflow: 'hidden' }}>
        {typed}<span className="crw-caret"></span>
      </span>
    </div>
  );
}

// Footage thumbnail — a real video still, cover-cropped. `pos` shifts the crop
// window and `zoom` scales into it, so several results from the SAME clip can
// show different moments of one image. `ar` (aspect-ratio) is preferred over a
// fixed height so thumbs keep the real 16:9 video proportion at any width.
export function Thumb({ img, pos = '50% 50%', zoom = 1, score, label, h, ar, delay = 0, style }: { img: string; pos?: string; zoom?: number; score?: number; label?: string; h?: number; ar?: string; delay?: number; style?: CSSProperties }) {
  return (
    <div className="crw-pop" style={{ animationDelay: `${delay}ms`, position: 'relative', height: h, aspectRatio: ar ?? (h == null ? '16 / 9' : undefined), borderRadius: 7, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 6px 16px rgba(0,0,0,0.35)', background: '#101013', ...style }}>
      <img src={img} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: pos, transform: zoom !== 1 ? `scale(${zoom})` : undefined, transformOrigin: pos, display: 'block' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 62%, rgba(0,0,0,0.32))' }}></div>
      {score != null ? <span style={{ position: 'absolute', top: 5, right: 5, fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 600, color: 'var(--signal-300)', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', padding: '1px 5px', borderRadius: 4, border: '1px solid rgba(var(--light-mid-rgb),0.3)' }}>{score}%</span> : null}
      {label ? <span style={{ position: 'absolute', left: 6, bottom: 5, fontFamily: 'var(--font-mono)', fontSize: 8.5, color: 'rgba(255,255,255,0.75)', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', padding: '1px 5px', borderRadius: 3 }}>{label}</span> : null}
    </div>
  );
}

// App window chrome for demo scenes — glass chrome inside a gradient ring.
export function PanelFrame({ title, children, badge, pad = 16 }: { title: string; children: ReactNode; badge?: string; pad?: number }) {
  const settled = useSettled();
  return (
    <div style={{ height: '100%', borderRadius: 16, padding: 1, background: 'linear-gradient(160deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04) 30%, rgba(var(--light-soft-rgb),0.25) 70%, rgba(255,255,255,0.06))', boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 90px rgba(var(--light-rgb),0.07)' }}>
      <div data-settled={settled ? 'true' : undefined} style={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 15, overflow: 'hidden', background: 'rgba(9,9,12,0.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '11px 15px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))' }}>
          <span style={{ display: 'flex', gap: 5 }}>
            {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => <i key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c, opacity: 0.8 }}></i>)}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.06em', color: 'var(--fg-3)', textTransform: 'uppercase', marginLeft: 6 }}>{title}</span>
          <span style={{ flex: 1 }}></span>
          {badge ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.08em', color: 'var(--fg-2)', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 5, background: 'rgba(255,255,255,0.06)', border: '1px solid var(--line-2)' }}>{badge}</span> : null}
        </div>
        <div style={{ flex: 1, padding: pad, minHeight: 0, position: 'relative', overflow: 'hidden' }}>{children}</div>
      </div>
    </div>
  );
}

/* ===== Real-app frame — mirrors the actual ClipRadar desktop app ===== */
export function AppFrame({ tab = 'Search', children, badge }: { tab?: string; children: ReactNode; badge?: string }) {
  const settled = useSettled();
  const tabs: [string, IconName][] = [['Search', 'search'], ['Media', 'film'], ['People', 'users'], ['Settings', 'gear'], ['Help', 'help']];
  return (
    <div style={{ height: '100%', borderRadius: 14, padding: 1, background: 'linear-gradient(160deg, rgba(255,255,255,0.16), rgba(255,255,255,0.05) 35%, rgba(var(--light-soft-rgb),0.2) 75%, rgba(255,255,255,0.06))', boxShadow: '0 40px 100px rgba(0,0,0,0.65), 0 0 110px rgba(var(--light-rgb),0.06)' }}>
      <div data-settled={settled ? 'true' : undefined} style={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 13, overflow: 'hidden', background: 'var(--app-bg)' }}>
        {/* titlebar */}
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: '#09090B', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <img src={logoMarkMono} width="12" height="12" alt="" style={{ opacity: 0.6 }} />
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>ClipRadar</span>
          <span style={{ flex: 1 }}></span>
          <span style={{ display: 'flex', gap: 14, color: 'var(--fg-4)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
            <i style={{ fontStyle: 'normal' }}>—</i><i style={{ fontStyle: 'normal' }}>□</i><i style={{ fontStyle: 'normal' }}>✕</i>
          </span>
        </div>
        {/* tab bar */}
        <div className="crw-app-tabs" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 4, padding: '7px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {tabs.map(([t, ic]) => {
            const on = t === tab;
            return (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 8, fontSize: 11.5, fontWeight: on ? 600 : 500, color: on ? 'var(--fg-1)' : 'var(--fg-3)', background: on ? '#27272A' : 'transparent', border: on ? '1px solid rgba(255,255,255,0.15)' : '1px solid transparent', boxShadow: on ? 'inset 0 1px 0 rgba(255,255,255,0.08)' : 'none' }}>
                <CRWIcon d={ic} size={12} />{t}
              </span>
            );
          })}
          <span style={{ flex: 1 }}></span>
          {badge ? <span className="crw-app-badge" style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.07em', color: 'var(--fg-2)', marginRight: 10 }}>{badge}</span> : null}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--app-green)', flex: 'none' }}>
            <i className="crw-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--app-green)', display: 'inline-block' }}></i> Online
          </span>
        </div>
        <div style={{ flex: 1, padding: 12, minHeight: 0, position: 'relative', overflow: 'hidden' }}>{children}</div>
      </div>
    </div>
  );
}

// Real-app search box: Visuals/Speech pills, dashed scope chips, typed query, ✕ + camera + blue submit.
export function AppSearchBox({ query, mode = 'Visuals', delay = 400, history = [], person, compact }: { query?: string; mode?: 'Visuals' | 'Speech'; delay?: number; history?: string[]; person?: string; compact?: boolean }) {
  const { typed, done } = useTyping(query || '', { delay });
  const modes: [string, IconName][] = [['Visuals', 'image'], ['Speech', 'mic']];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 2, padding: 2, borderRadius: 9, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', alignSelf: 'flex-start' }}>
        {modes.map(([m, ic]) => (
          <span key={m} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 7, fontSize: 11.5, fontWeight: mode === m ? 600 : 500, color: mode === m ? 'var(--fg-1)' : 'var(--fg-3)', background: mode === m ? '#27272A' : 'transparent', border: `1px solid ${mode === m ? 'rgba(255,255,255,0.12)' : 'transparent'}` }}>
            <CRWIcon d={ic} size={11} />{m}
          </span>
        ))}
      </div>
      <div style={{ borderRadius: 12, background: '#0F0F10', border: '1px solid', borderColor: done && query ? 'rgba(96,158,250,0.55)' : 'rgba(255,255,255,0.1)', boxShadow: done && query ? '0 0 0 3px rgba(96,158,250,0.12), 0 0 22px rgba(96,158,250,0.12)' : 'none', transition: 'border-color 300ms var(--ease-out), box-shadow 300ms var(--ease-out)', padding: '9px 10px' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: compact ? 6 : 9, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 6, fontSize: 10.5, fontWeight: 500, whiteSpace: 'nowrap', color: 'var(--fg-1)', background: 'rgba(39,39,42,0.6)', border: '1px dashed rgba(255,255,255,0.12)' }}><CRWIcon d="at" size={11} /> All analyzed media</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: person ? 999 : 6, fontSize: 10.5, fontWeight: 500, whiteSpace: 'nowrap', color: person ? '#9EC2FF' : 'var(--fg-2)', background: person ? 'rgba(51,104,208,0.18)' : 'rgba(39,39,42,0.6)', border: person ? '1px solid rgba(96,158,250,0.45)' : '1px dashed rgba(255,255,255,0.12)' }}><CRWIcon d="user" size={11} /> {person || 'People'}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 6, fontSize: 10.5, fontWeight: 500, whiteSpace: 'nowrap', color: 'var(--fg-4)', background: 'rgba(39,39,42,0.6)', border: '1px dashed rgba(255,255,255,0.12)' }}><CRWIcon d="ban" size={11} /> Exclude</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: typed ? 'var(--fg-1)' : 'var(--fg-4)', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            {typed || 'Search your footage…'}{typed && !done ? <span className="crw-caret"></span> : null}
          </span>
          {typed && done ? <span style={{ color: 'var(--fg-4)', display: 'inline-flex' }}><CRWIcon d="x" size={13} /></span> : null}
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 26, borderRadius: 7, border: '1px solid rgba(255,255,255,0.12)', color: 'var(--fg-3)' }}><CRWIcon d="camera" size={13} /></span>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 28, borderRadius: 8, background: 'var(--app-blue)', color: '#F2F9FF', boxShadow: '0 2px 8px rgba(51,104,208,0.45)' }}><CRWIcon d="arrowRight" size={13} sw={2.2} /></span>
        </div>
      </div>
      {history.length ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {history.map((h) => (
            <span key={h} style={{ fontSize: 10, color: 'var(--fg-3)', padding: '3px 9px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.06)' }}>{h}</span>
          ))}
          <span style={{ flex: 1 }}></span>
          <span style={{ fontSize: 10.5, color: 'var(--fg-3)', fontWeight: 500 }}>Clear</span>
        </div>
      ) : null}
    </div>
  );
}

// Result group — “query” in N files + actions, then a grid of result cards.
export function ResultGroup({ query, files = 4, children, delay = 1500 }: { query: string; files?: number; children: ReactNode; delay?: number }) {
  return (
    <div className="crw-fade" style={{ animationDelay: `${delay}ms`, marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--fg-2)', flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>“{query}” <i style={{ fontStyle: 'normal', color: 'var(--fg-4)' }}>in {files} files</i></span>
        <span style={{ color: 'var(--fg-4)', display: 'inline-flex', flex: 'none' }}><CRWIcon d="chevdown" size={12} /></span>
        <span style={{ flex: 'none', fontSize: 10, color: 'var(--fg-3)', padding: '3px 9px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', whiteSpace: 'nowrap' }}>Copy 10 to clipboard</span>
      </div>
      {children}
    </div>
  );
}

// One result card — thumb + checkbox + duration, filename + timecode range below.
export function ResultCard({ img, pos, zoom, file, tc, dur, delay = 0, selected, style }: { img: string; pos?: string; zoom?: number; file?: string; tc?: string; dur?: string; delay?: number; selected?: boolean; style?: CSSProperties }) {
  return (
    <div className="crw-pop" style={{ animationDelay: `${delay}ms`, position: 'relative', ...style }}>
      <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: selected ? '1.5px solid var(--app-blue)' : '1px solid rgba(255,255,255,0.1)', boxShadow: selected ? '0 0 14px rgba(59,110,246,0.35)' : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 6px 16px rgba(0,0,0,0.35)' }}>
        <Thumb img={img} pos={pos} zoom={zoom} delay={0} style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }} />
        <span style={{ position: 'absolute', top: 5, left: 5, width: 11, height: 11, borderRadius: 3, border: '1.5px solid rgba(255,255,255,0.5)', background: 'rgba(0,0,0,0.4)' }}></span>
        {dur ? <span style={{ position: 'absolute', right: 5, bottom: 5, fontFamily: 'var(--font-mono)', fontSize: 8.5, fontWeight: 600, color: '#fff', background: 'rgba(0,0,0,0.75)', padding: '1px 5px', borderRadius: 4 }}>{dur}</span> : null}
      </div>
      {file ? <div style={{ fontSize: 9.5, fontWeight: 500, color: 'var(--fg-1)', marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file}</div> : null}
      {tc ? <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8.5, color: '#7FA8F8', marginTop: 2 }}>{tc}</div> : null}
    </div>
  );
}

export function MonoNote({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.06em', color: 'var(--fg-3)', textTransform: 'uppercase', ...style }}>{children}</span>;
}

// Email capture — used in hero, final CTA + about page. POSTs via the shared
// waitlist hook (Worker → D1 + Resend) and navigates to /joined on success.
export function EmailCapture({ size = 'md', metal, tier = 'general', source }: { size?: 'md' | 'lg'; metal?: boolean; tier?: WaitlistTier; source?: string }) {
  const [email, setEmail] = useState('');
  const [trap, setTrap] = useState('');
  const { state, submit, clearError } = useWaitlistSubmit(tier, source);
  const join = () => { void submit(email, trap); };
  const Btn = metal ? MetalBtn : GradBtn;
  const label = state === 'sending' ? 'Requesting…' : 'Request early access';
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', flexWrap: 'wrap', justifyContent: 'center' }}>
      <input
        value={email}
        onChange={(e) => { setEmail(e.target.value); clearError(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') join(); }}
        placeholder="you@studio.com"
        type="email"
        aria-label="Email address"
        aria-invalid={state === 'error'}
        className="crw-input"
        style={{ flex: '1 1 230px', maxWidth: 420, minWidth: 0, boxSizing: 'border-box', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg-1)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: size === 'lg' ? '13px 16px' : '12px 14px', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)' }}
      />
      {/* honeypot — invisible to humans, bots fill it */}
      <input
        value={trap}
        onChange={(e) => setTrap(e.target.value)}
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
      <Btn size={size} onClick={join}>{label}</Btn>
      {state === 'error' && (
        <span role="alert" style={{ flexBasis: '100%', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.04em', color: '#f87171' }}>
          That didn’t go through — check the email and try again.
        </span>
      )}
    </div>
  );
}
